import React, { useState, useEffect } from "react";
import "../CSS/TestSeriesPage.css";
import Navbar1 from "../Components/Header";
import Footer from "../Components/Footer";
import Loader from "../Components/Loader";
import image1 from "../Images/HotelManagement.png";
import image2 from "../Images/HospitalManagement.png";
import image3 from "../Images/EduTechApp.png";
import image4 from "../Images/SkillsTraining.png";

import ts1 from "../Images/ts1.png";
import ts2 from "../Images/ts2.png";
import ts3 from "../Images/ts3.png";
import ts4 from "../Images/ts4.png";
import ts5 from "../Images/ts5.png";
import ts6 from "../Images/ts6.png";
import ts7 from "../Images/ts7.png";

// Image Slider Component
const ImageSlider = ({ images, interval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, interval);

    return () => clearInterval(timer);
  }, [images.length, interval]);

  return (
    <div className="test-series-image-slider">
      <div className="test-series-slider-wrapper">
        {images.map((image, index) => (
          <img
            key={index}
            src={image}
            alt={`Slide ${index + 1}`}
            loading="lazy"
            className={`test-series-slider-image ${index === currentIndex ? 'slider-active' : ''}`}
          />
        ))}
      </div>
      
      <div className="slider-indicators">
        {images.map((_, index) => (
          <span
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

const TestSeriesPage = () => {
  const [loading, setLoading] = useState(false);

  // Section 1 Data
  const introData = {
    features: [
      "100% textbook-aligned automation — no generic content",
      "Minutes, not hours to create assessments and learning material",
      "Built for continuous, low-stress evaluation, not exam cramming",
      "Uniform academic standards across sections and grades",
      "Technology that supports teachers, not replaces them"
    ],
    images: [
      ts7,
      "https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=800&h=600&fit=contain",
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=contain"
    ]
  };

  // Section 2 Data
  const modulesData = {
    modules: [
      {
        title: "Module 1: Assessment & Evaluation Module (Test Series)",
        description: "This module enables teachers to generate structured assessments directly from prescribed textbooks. It standardizes test creation while giving teachers full control over patterns, difficulty, and final output. Evaluation insights help identify learning gaps early and accurately."
      },
      {
        title: "Module 2: Remedial Learning Module (Remedial Lab)",
        description: "Designed to support poor performers, this module converts assessment gaps into targeted remedial plans. Teachers can instantly create textbook-aligned, simplified learning materials—worksheets, quizzes, and guided practice and can monitor improvement over time."
      }
    ],
    images: [
      ts1,
      ts2,
      ts3
    ]
  };

  // Section 3 Data
  const adaptiveData = {
    cards: [
      {
        title: "Flash Cards (Concept Reinforcement)",
        points: [
          "Bite-sized, textbook-aligned concept summaries for quick recall",
          "Each card focuses on one core idea (definitions, properties, classifications)",
          "Designed for revision, last-minute preparation, and memory reinforcement",
          "Visually structured to reduce cognitive load and improve retention"
        ]
      },
      {
        title: "Ready Reckoner (Structured Concept Overview)",
        points: [
          "Concise, exam-oriented chapter snapshot covering all key ideas",
          "Organizes content into core concepts, techniques, and applications",
          "Ideal for pre-exam revision and teacher-led recap sessions"
        ]
      }
    ],
    images: [
      ts4,
      ts5,
      ts6
    ]
  };

  return (
    <>
      <Loader />
      <div className="test-series-page">
        <Navbar1 />
        
        {/* Hero Section */}
        <section className="test-series-hero">
          <div className="container">
            <div className="test-series-hero-content">
              <span className="test-series-badge text-white">AI-Powered Learning</span>
              <h4 style={{color:"white"}} className="test-series-hero-title">
                Test Series Kit – Textbook-Aligned Assessment & Learning
              </h4>
              <p className="test-series-hero-description">
                Transform existing textbooks into structured tests, adaptive content, and remedial learning workflows—reducing teacher workload while improving academic consistency.
              </p>
            </div>
          </div>
        </section>

        {/* Section 1: Introduction */}
        <section className="section-introduction">
          <div className="container">
            <div className="intro-layout">
              <div className="intro-content">
                <h3 className="intro-heading">Brief Introduction</h3>
                <p className="intro-text">
                  Test Series AI is a purpose-built, textbook-aligned assessment and learning automation kit designed for schools. It transforms existing textbooks into structured tests, adaptive content, and remedial learning workflows—reducing teacher workload while improving academic consistency, transparency, and student outcomes.
                </p>
                
                <h4 className="intro-subheading">What Makes Us Different</h4>
                <ul className="intro-list">
                  {introData.features.map((feature, idx) => (
                    <li key={idx} className="intro-list-item">
                      <span className="material-symbols-outlined list-icon">
                        check_circle
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="intro-image">
                <ImageSlider images={introData.images} interval={3000} />
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Module 1 & 2 Cards + Image (2 Columns) */}
        <section className="section-modules">
          <div className="container">
            <div className="modules-layout">
              <div className="modules-cards-column">
                {modulesData.modules.map((module, idx) => (
                  <div key={idx} className="module-card">
                    <h3 className="module-card-title">{module.title}</h3>
                    <p className="module-card-text">{module.description}</p>
                  </div>
                ))}
              </div>

              <div className="modules-image-column">
                <ImageSlider images={modulesData.images} interval={3000} />
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Module 3 Content + Cards + Image (2 Columns) */}
        <section className="section-adaptive">
          <div className="container">
            <div className="adaptive-layout">
              <div className="adaptive-content-column">
                <div className="adaptive-header">
                  <h2 className="adaptive-title">Module 3: Adaptive Content Module (Revision & Classroom Aids)</h2>
                  <p className="adaptive-description">
                    This module supports diverse learners by creating multiple content formats from the same textbook chapter. It helps teachers address different learning needs inside and outside the classroom.
                  </p>
                </div>

                <div className="adaptive-cards-stack">
                  {adaptiveData.cards.map((card, idx) => (
                    <div key={idx} className="adaptive-card">
                      <h4 className="adaptive-card-title">{card.title}</h4>
                      <ul className="adaptive-card-list">
                        {card.points.map((point, pointIdx) => (
                          <li key={pointIdx} className="adaptive-card-item">
                            <span className="material-symbols-outlined item-icon">
                              arrow_right
                            </span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="adaptive-image-column">
                <ImageSlider images={adaptiveData.images} interval={3000} />
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    </>
  );
};

export default TestSeriesPage;
